import { useState, useEffect, useCallback, useRef } from 'react'
import debounce from 'lodash.debounce'
import lambdaFetch from '../functions/FetchFromLambda'

// API configuration
const API_CONFIG = {
  path: 'opensearch',
  method: 'post',
  defaultDebounceMs: 600
}

/**
 * Custom hook for managing search state and API calls
 */
const useOpenSearch = ({
  page,
  entity,
  searchFields,
  accessToken,
  credentials,
  debounceMs = API_CONFIG.defaultDebounceMs,
  filters = {},
  clearTrigger = false
}) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [selectedValue, setSelectedValue] = useState(null)
  const [touched, setTouched] = useState(false)
  const [isIndexNotFound, setIsIndexNotFound] = useState(false)
  
  const abortControllerRef = useRef(null)
  const requestSequenceRef = useRef(0)
  const isMountedRef = useRef(true)
  const searchCache = useRef(new Map())
  const previousFiltersRef = useRef(JSON.stringify(filters))

  // Function to clear cache entries that are no longer relevant
  const clearStaleCache = useCallback(() => {
    const currentKeyPrefix = `${page}:${entity}:`
    const keysToDelete = []
    
    // Find all cache keys that don't match the current page/entity combination
    for (const key of searchCache.current.keys()) {
      if (!key.startsWith(currentKeyPrefix)) {
        keysToDelete.push(key)
      }
    }
    
    // Remove stale entries
    keysToDelete.forEach(key => searchCache.current.delete(key))
  }, [page, entity])

  // Function to clear cache when filters change
  const clearCacheOnFilterChange = useCallback(() => {
    // Clean filters to normalize null/undefined/empty values for comparison
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value
      }
      return acc
    }, {})
    const currentFilters = JSON.stringify(cleanFilters)
    const previousFilters = previousFiltersRef.current
    
    // If filters have changed, clear all cache entries for this hook instance
    if (currentFilters !== previousFilters) {
      searchCache.current.clear()
      setOptions([]) // Clear current options to force fresh data
      previousFiltersRef.current = currentFilters
    }
  }, [filters])

  const searchItems = useCallback(async (searchTerm = '') => {
    const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value
      }
      return acc
    }, {})

    const cacheKey = `${page}:${entity}:${searchTerm}:${JSON.stringify(cleanFilters)}:${JSON.stringify(searchFields)}`

    if (searchCache.current.has(cacheKey)) {
      setOptions(searchCache.current.get(cacheKey))
      return
    }

    // Cancel previous in-flight request at the network level
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    // Sequence number guards against stale responses that slip past the abort
    const currentSequence = ++requestSequenceRef.current

    if (isMountedRef.current) setLoading(true)

    try {
      const requestBody = {
        page,
        entity,
        filters: cleanFilters,
        searchData: {}
      }

      if (searchTerm) {
        searchFields.forEach(field => {
          if (field) {
            requestBody.searchData[field] = searchTerm
          }
        })
      }

      const response = await lambdaFetch(
        API_CONFIG.path,
        API_CONFIG.method,
        accessToken,
        JSON.stringify(requestBody),
        '',
        credentials,
        false,
        '',
        abortController.signal
      )

      // Discard if this request was aborted or a newer request has already started
      if (abortController.signal.aborted) return
      if (!isMountedRef.current || currentSequence !== requestSequenceRef.current) return

      if (response === false) {
        if (isMountedRef.current) {
          setIsIndexNotFound(false)
          setOptions([])
        }
        return
      }

      let errorMessage = null
      if (response?.error) {
        errorMessage = response.error
      } else if (response?.data?.error) {
        errorMessage = response.data.error
      } else if (response?.data?.response?.body) {
        try {
          const parsedBody = typeof response.data.response.body === 'string'
            ? JSON.parse(response.data.response.body)
            : response.data.response.body
          if (parsedBody?.error) {
            errorMessage = parsedBody.error
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      if (errorMessage && typeof errorMessage === 'string' && errorMessage.includes('index_not_found_exception')) {
        console.error('[OpenSearch] Index not found error:', errorMessage)
        if (isMountedRef.current) {
          setIsIndexNotFound(true)
          setOptions([])
        }
        return
      }

      if (isMountedRef.current) {
        setIsIndexNotFound(false)

        let items = []
        if (response?.items) {
          items = response.items
        } else if (response?.data?.items) {
          items = response.data.items
        } else if (response?.data) {
          items = Array.isArray(response.data) ? response.data : []
        } else {
          console.warn('No items found in response:', response)
        }

        searchCache.current.set(cacheKey, items)
        setOptions(items)
      }

    } catch (error) {
      if (error.name === 'AbortError' || abortController.signal.aborted) return

      const errorMessage = error.message || error.toString()
      if (isMountedRef.current && currentSequence === requestSequenceRef.current) {
        if (errorMessage.includes('index_not_found_exception')) {
          console.error('[OpenSearch] Index not found error:', errorMessage)
          setIsIndexNotFound(true)
        } else {
          console.error('Search failed:', error)
          setIsIndexNotFound(false)
        }
        setOptions([])
      }
    } finally {
      // Only update loading if this is still the active request
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
        if (isMountedRef.current) setLoading(false)
      }
    }
  }, [page, entity, searchFields, accessToken, credentials, filters])

  // Clear stale cache when page or entity changes
  useEffect(() => {
    clearStaleCache()
  }, [clearStaleCache])

  // Clear cache when filters change
  useEffect(() => {
    clearCacheOnFilterChange()
  }, [clearCacheOnFilterChange])

  // Clear dropdown when clearTrigger changes
  useEffect(() => {
    if (clearTrigger) {
      setSelectedValue(null)
      setInputValue('')
      setTouched(false)
      setIsIndexNotFound(false) // Reset index not found flag
    }
  }, [clearTrigger])

  // Cleanup function
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      searchCache.current.clear()
    }
  }, [])

  const debouncedSearch = useCallback(
    debounce((term) => {
      searchItems(term)
    }, debounceMs),
    [searchItems, debounceMs]
  )

  const handleInputChange = useCallback((newInputValue) => {
    setInputValue(newInputValue)
    debouncedSearch(newInputValue.trim())
  }, [debouncedSearch])

  const handleChange = useCallback((newValue, onChange) => {
    setSelectedValue(newValue)
    setTouched(true)
    if (onChange) {
      onChange(newValue)
    }
    if (!newValue) {
      searchItems('')
    }
  }, [searchItems])

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !loading) {
      searchItems('')
    }
  }, [options.length, loading, searchItems])

  const handleBlur = useCallback(() => {
    // Only mark as touched when user leaves the field without selecting
    if (!selectedValue) {
      setTouched(true)
    }
  }, [selectedValue])

  const resetTouched = useCallback(() => {
    setTouched(false)
  }, [])

  const setValue = useCallback((value) => {
    setSelectedValue(value)
  }, [])

  const setInputValueDirect = useCallback((value) => {
    setInputValue(value)
  }, [])

  const clearCache = useCallback(() => {
    searchCache.current.clear()
    setOptions([]) // Also clear current options
  }, [])

  const refreshSearch = useCallback(() => {
    clearCache()
    searchItems(inputValue) // Re-run search with current input value
  }, [clearCache, searchItems, inputValue])

  return {
    // State
    loading,
    options,
    inputValue,
    selectedValue,
    touched,
    isIndexNotFound,
    
    // Methods
    searchItems,
    handleInputChange,
    handleChange,
    handleOpen,
    handleBlur,
    resetTouched,
    setValue,
    setInputValueDirect,
    clearCache,
    refreshSearch
  }
}

export default useOpenSearch
